import type { OrderDetails } from '@/entities/order/model'
import { useCallback } from 'react'
import { getOrderType } from '@/entities/order/lib'
import { useCollectionCodeEnabled, useStateConfigQuery } from '@/entities/state/model'

/**
 * The collection code completion gate.
 *
 * Shared by every route that can complete an order — quick actions, the manual
 * status routes and the end of the Proof of Age checks (BD-8761) — so they all
 * agree on which orders must be verified before they complete.
 */
export function useCollectionCodeGate() {
  const isCollectionCodeEnabled = useCollectionCodeEnabled()
  const { data: stateConfig } = useStateConfigQuery()
  const eligibleOrderTypes = stateConfig?.collectCodeEligibleOrderTypes

  /** Whether completing this order must go through collection code verification. */
  const requiresCollectionCode = useCallback((order?: OrderDetails): boolean => {
    if (!isCollectionCodeEnabled || !order) {
      return false
    }
    const normalizedOrderType = getOrderType(order.hsFields)?.trim().toLowerCase()
    const configuredEligibleOrderTypes = (eligibleOrderTypes ?? [])
      .map(type => type.trim().toLowerCase())
      .filter(Boolean)
    return configuredEligibleOrderTypes.includes(normalizedOrderType ?? '')
  }, [isCollectionCodeEnabled, eligibleOrderTypes])

  return { requiresCollectionCode }
}
